import { Button, InputNumber, Space, Switch, Typography } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { NumberFilterProps, NumberFilterValue } from './types';

import s from './NumberFilter.module.scss';

const NumberFilter: React.FC<NumberFilterProps> = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  close,
  isPercent,
}) => {
  const { t } = useTranslation();
  const currentValue: NumberFilterValue = React.useMemo(() => {
    if (selectedKeys.length > 0 && typeof selectedKeys[0] === 'string') {
      try {
        return JSON.parse(selectedKeys[0]) as NumberFilterValue;
      } catch {
        return {};
      }
    }
    return {};
  }, [selectedKeys]);

  const [from, setFrom] = React.useState<number | undefined>(currentValue.from);
  const [to, setTo] = React.useState<number | undefined>(currentValue.to);
  const [strict, setStrict] = React.useState<boolean>(currentValue.strict ?? false);

  React.useEffect(() => {
    setFrom(currentValue.from);
    setTo(currentValue.to);
    setStrict(currentValue.strict ?? false);
  }, [currentValue]);

  const handleApply = React.useCallback(() => {
    const filterValue: NumberFilterValue = {};
    
    if (from !== undefined && from !== null) {
      filterValue.from = from;
    }
    if (to !== undefined && to !== null) {
      filterValue.to = to;
    }
    if (strict) {
      filterValue.strict = strict;
    }

    if (Object.keys(filterValue).length > 0) {
      setSelectedKeys([JSON.stringify(filterValue)]);
    } else {
      setSelectedKeys([]);
    }
    
    confirm();
  }, [from, to, strict, setSelectedKeys, confirm]);

  const handleReset = React.useCallback(() => {
    setFrom(undefined);
    setTo(undefined);
    setStrict(false);
    clearFilters?.();
  }, [clearFilters]);

  return (
    <div className={s.numberFilter}>
      <Space direction="vertical" size="small" className={s.numberFilter__content}>
        <div className={s.numberFilter__row}>
          <Typography.Text className={s.numberFilter__label}>{t('filter.from')}</Typography.Text>
          <InputNumber
            value={from}
            onChange={(value) => setFrom(value ?? undefined)}
            placeholder={t('filter.min') as string}
            className={s.numberFilter__input}
            addonAfter={isPercent ? '%' : undefined}
          />
        </div>
        
        <div className={s.numberFilter__row}>
          <Typography.Text className={s.numberFilter__label}>{t('filter.to')}</Typography.Text>
          <InputNumber
            value={to}
            onChange={(value) => setTo(value ?? undefined)}
            placeholder={t('filter.max') as string}
            className={s.numberFilter__input}
            addonAfter={isPercent ? '%' : undefined}
          />
        </div>

        <div className={s.numberFilter__row}>
          <Typography.Text className={s.numberFilter__label}>{t('filter.strict')}</Typography.Text>
          <Switch
            checked={strict}
            onChange={setStrict}
            size="small"
          />
          <Typography.Text type="secondary" className={s.numberFilter__hint}>
            {strict ? '(> & <)' : '(≥ & ≤)'}
          </Typography.Text>
        </div>
      </Space>

      <Space className={s.numberFilter__buttons}>
        <Button size="small" onClick={handleReset}>
          {t('filter.reset')}
        </Button>
        <Button type="primary" size="small" onClick={handleApply}>
          {t('filter.apply')}
        </Button>
      </Space>
    </div>
  );
};

export default NumberFilter;
